// src/lib/cross-surface.ts
//
// Post-INF-4 dual-origin routing helpers.
//
// In the post-INF-4 CloudFront layout (perch-mobile commit `29181e1`,
// staging distribution `E37KPHTREK7TQG`), the following path prefixes
// route to the APP-origin S3 bucket
// (staging.perchmobile.com / production.perchmobile.com) and are
// served by the perch-mobile SPA bundle. Anything else routes to the
// marketing-origin bucket (this repo's bundle).
//
// The marketing SPA (this bundle) does NOT have React Router routes
// for any of these paths — they were stripped in EXT-1b. So a
// react-router-dom soft navigation via `navigate()` or `<Link>` to
// one of these paths falls through to the catch-all `<NotFound>` (or
// throws via Sentry's ErrorBoundary), manifesting as a broken page
// until the user manually refreshes (at which point the browser does
// a hard request and CloudFront routes the user to the app bundle).
//
// Solution: force a full-page navigation (browser-driven request)
// from the marketing surface to any app route. This module supplies
// the two primitives:
//
//   - `appNavigate(path)` — imperative replacement for `navigate()`
//   - `<AppLink to=...>` — declarative replacement for `<Link>` (in
//     `src/components/AppLink.tsx`)
//
// Keep `APP_ROUTE_PREFIXES` in sync with:
//   perch-mobile/.github/workflows/deploy-staging.yml (post-deploy
//     invalidation path list)
//   perch-mobile/docs/marketing-split/prompts/slice-I-cloudfront-infra.md
//     (the cache-behavior table)

/**
 * App-origin route prefixes in the post-INF-4 dual-origin layout.
 * Matches the path-based cache behaviors on the staging distribution
 * `E37KPHTREK7TQG` and the production distribution scheduled to be
 * cut over after the 24h soak.
 *
 * Order does not matter (matching is by prefix-equality or
 * prefix+'/'-startsWith). Add or remove entries here when the
 * CloudFront cache-behavior list changes on the perch-mobile side.
 */
export const APP_ROUTE_PREFIXES: ReadonlyArray<string> = [
  "/checkout",
  "/device-check",
  "/customer-portal",
  "/payment-success",
  "/payment-issue",
  "/email-verified",
  "/set-password",
  "/login",
  "/forgot-password",
  "/activation-guide",
  "/admin",
  "/account-confirmation",
  "/sentry-test",
];

/**
 * Returns true when `path` (a pathname optionally followed by a
 * `?query` or `#hash`) targets an app-origin route. Returns false
 * for "/", any marketing-origin route, malformed input, or
 * non-string input.
 *
 * Examples:
 *   isAppRoute("/checkout")                                 // true
 *   isAppRoute("/checkout?planId=1974&billingInterval=...") // true
 *   isAppRoute("/login")                                    // true
 *   isAppRoute("/login/forgot-password")                    // true
 *   isAppRoute("/admin/accounts/123")                       // true
 *   isAppRoute("/plans")                                    // false
 *   isAppRoute("/plan-summary?plan=1974")                   // false
 *   isAppRoute("/")                                         // false
 */
export function isAppRoute(path: string): boolean {
  if (typeof path !== "string" || path.length === 0) return false;
  // Use the URL parser with a throwaway base to handle ?query and
  // #hash uniformly without hand-rolling a parser. The base is only
  // needed because the URL constructor requires an absolute URL.
  let pathname: string;
  try {
    pathname = new URL(path, "https://_/").pathname;
  } catch {
    return false;
  }
  return APP_ROUTE_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(prefix + "/"),
  );
}

/**
 * Imperative full-page navigation to `path`. Use in place of
 * react-router-dom's `navigate(path)` whenever the target is an
 * app route — the full-page request lets CloudFront's path-based
 * routing select the app-origin bucket and serve the app SPA
 * bundle, which has React Router routes for `/checkout` etc.
 *
 * Idempotent in non-browser contexts (no-op when `window` is
 * undefined, e.g. SSR or test environments without a JSDOM).
 */
export function appNavigate(path: string): void {
  if (typeof window === "undefined") return;
  window.location.assign(path);
}
